<template>
  <Dialog
    maximizable
    v-model:visible="visible"
    modal
    :header="lang.t('views.users.user')"
    class="!w-[90vw] lg:!w-[40rem]"
  >
    <form @submit.prevent="submitForm" class="flex flex-col gap-4 text-sky-900 dark:text-sky-100">
      <div class="flex flex-col gap-1">
        <label class="font-bold">{{ lang.t('views.users.nameLabel') }} <span class="text-red-500">*</span></label>
        <InputText
          v-model="form.name"
          class="w-full"
          :class="{ 'p-invalid': submitted && !validName(form.name) }"
        />
        <small v-if="submitted && !validName(form.name)" class="text-red-500">
          {{ lang.t('views.users.nameMinLength') }}
        </small>
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-bold">{{ lang.t('views.users.emailLabel') }}</label>
        <InputText v-model="form.email" disabled class="w-full opacity-60" />
      </div>

      <template v-if="isNormal">
        <div class="flex flex-col gap-1">
          <label class="font-bold">{{ lang.t('views.users.passwordLabel') }}</label>
          <InputText
            type="password"
            v-model="form.password"
            class="w-full"
            :placeholder="lang.t('views.users.passwordOptionalPlaceholder')"
            :class="{ 'p-invalid': submitted && shouldValidatePassword(form.password) && !validPassword(form.password) }"
          />
          <small v-if="submitted && shouldValidatePassword(form.password) && !validPassword(form.password)" class="text-red-500">
            {{ lang.t('views.users.passwordRequirements') }}
          </small>
        </div>

        <div class="flex flex-col gap-1" v-if="form.password">
          <label class="font-bold">{{ lang.t('views.users.confirmPasswordLabel') }} <span class="text-red-500">*</span></label>
          <InputText
            type="password"
            v-model="form.confirmPassword"
            class="w-full"
            :class="{ 'p-invalid': submitted && form.password !== form.confirmPassword }"
          />
          <small v-if="submitted && form.password !== form.confirmPassword" class="text-red-500">
            {{ lang.t('views.users.passwordMismatch') }}
          </small>
        </div>
      </template>

      <div class="flex flex-col gap-1">
        <label class="font-bold">{{ lang.t('views.users.descriptionLabel') }}</label>
        <InputText v-model="form.description" class="w-full" />
      </div>

      <div v-if="!isSelfEdit" class="flex flex-col gap-1">
        <label class="font-bold">{{ lang.t('views.users.roleLabel') }}</label>
        <Select
          v-model="selectedRole"
          option-label="label"
          :options="roleOptions"
          :placeholder="lang.t('views.users.selectRolePlaceholder')"
          class="w-full"
        />
      </div>

      <div class="flex items-center gap-2">
        <Checkbox v-model="form.receiveEmails" binary input-id="receiveEmails" />
        <label for="receiveEmails" class="font-bold cursor-pointer">{{ lang.t('views.users.receiveEmailsLabel') }}</label>
      </div>
    </form>

    <template v-if="clientOptions !== undefined">
      <Divider />
      <div class="flex flex-col gap-3">
        <label class="font-bold">{{ lang.t('views.users.clientLabel') }}</label>

        <div v-if="loadingClients" class="flex justify-center py-3">
          <i class="pi pi-spin pi-spinner text-gray-400 text-xl" />
        </div>
        <div v-else class="flex flex-col gap-1 max-h-44 overflow-y-auto">
          <div
            v-for="client in userClients"
            :key="client.id"
            class="flex items-center justify-between px-2 py-1 rounded hover:bg-gray-50"
          >
            <span class="text-sm">{{ client.fullName }}</span>
            <Button
              icon="pi pi-times"
              severity="danger"
              text
              rounded
              size="small"
              @click="emit('removeClient', props.user?.id, client.id)"
            />
          </div>
          <div v-if="!userClients?.length" class="text-sm text-gray-400 text-center py-2">
            {{ lang.t('views.users.noClients') }}
          </div>
        </div>

        <div class="flex gap-2">
          <MultiSelect
            v-model="clientsToAdd"
            :options="availableClients"
            option-label="fullName"
            option-value="id"
            :placeholder="lang.t('views.users.addClientPlaceholder')"
            display="chip"
            class="flex-1"
          />
          <Button
            icon="pi pi-plus"
            :disabled="!clientsToAdd.length"
            @click="submitAddClients"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex w-full justify-end gap-2">
        <Button
          :label="lang.t('views.users.updateButton')"
          class="w-48"
          @click="submitForm"
          :loading="loading"
          :disabled="loading || !hasChanges"
        >
          <template #icon>
            <LoaderCircle v-if="loading" class="animate-spin" />
          </template>
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { AuthUserType, UserRole, getUserRoleLabel, isAuthType } from '@/models/user'
import { useUserFormValidation } from '@/composables/useUserFormValidation'
import { useLanguageStore } from '@/stores/LanguageStore'
import { LoaderCircle } from 'lucide-vue-next'
import { Button, Checkbox, Dialog, Divider, InputText, MultiSelect, Select } from 'primevue'
import { computed, ref } from 'vue'

const lang = useLanguageStore()
const { validName, validPassword, shouldValidatePassword } = useUserFormValidation()

const props = defineProps<{
  showModal: boolean
  user: any
  loading?: boolean
  isCurrentUserRoot?: boolean
  isSelfEdit?: boolean
  clientOptions?: Array<{ id: string; fullName: string }>
  userClients?: Array<{ id: string; fullName: string }>
  loadingClients?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', form: any): void
  (e: 'addClients', userId: string, clientIds: string[]): void
  (e: 'removeClient', userId: string, clientId: string): void
}>()

const submitted = ref(false)
const isNormal = computed(() => !isAuthType(props.user?.userType, AuthUserType.LDAP))

const clientsToAdd = ref<string[]>([])

const availableClients = computed(() => {
  const linked = new Set((props.userClients ?? []).map(c => c.id))
  return (props.clientOptions ?? []).filter(c => !linked.has(c.id))
})

function submitAddClients() {
  if (!clientsToAdd.value.length || !props.user?.id) return
  emit('addClients', props.user.id, clientsToAdd.value)
  clientsToAdd.value = []
}

const form = ref({
  name: props.user?.name ?? '',
  email: props.user?.email ?? '',
  password: '',
  confirmPassword: '',
  description: props.user?.description ?? '',
  userRole: props.user?.userRole ?? UserRole.User,
  receiveEmails: props.user?.receiveEmails ?? false,
})

const roleOptions = computed(() =>
  Object.values(UserRole)
    .filter((v): v is UserRole => Number.isInteger(v) && v !== UserRole.Sap && v !== UserRole.Erector && (props.isCurrentUserRoot || v !== UserRole.Root))
    .map((v) => ({ label: getUserRoleLabel(v), value: v }))
)

const selectedRole = computed({
  get: () => roleOptions.value.find((o) => o.value === form.value.userRole) ?? roleOptions.value[0],
  set: (val) => { if (val) form.value.userRole = val.value },
})

const visible = computed({
  get: () => props.showModal,
  set: (val: boolean) => { if (!val) emit('close') },
})

const hasChanges = computed(() => {
  return (
    form.value.name !== (props.user?.name ?? '') ||
    form.value.description !== (props.user?.description ?? '') ||
    (!props.isSelfEdit && form.value.userRole !== (props.user?.userRole ?? UserRole.User)) ||
    form.value.receiveEmails !== (props.user?.receiveEmails ?? false) ||
    (isNormal.value && form.value.password !== '')
  )
})

const submitForm = () => {
  submitted.value = true

  if (!validName(form.value.name)) return
  if (isNormal.value && shouldValidatePassword(form.value.password)) {
    if (!validPassword(form.value.password)) return
    if (form.value.password !== form.value.confirmPassword) return
  }

  emit('save', { ...form.value, id: props.user?.id })
}
</script>
